// Default browse (no query, no refinement, no saved filter) hides bare sources with a `filters`
// clause that any deliberate input lifts. The decision is made here, per outgoing request, rather
// than in a React component: a component only learns about a click after re-rendering, so every
// first click from default browse used to search once with the stale clause and again without it.
//
// DefaultIndexConfigure marks its index's requests with DEFAULT_BROWSE_PARAM; this client strips
// the marker and adds the clause when the request is default browse. While the filter panel is
// open it also adds UNFILTERED_FACETS_PARAM: default browse then takes its facets from a twin query
// without the clause. Facets computed under the clause under-count and miss values that a click
// (which lifts it) reveals, e.g. a region checkbox selecting only the listed countries and turning
// partial once the response adds the bare-only ones.

export const DEFAULT_BROWSE_PARAM = "qwotedDefaultBrowse";
export const UNFILTERED_FACETS_PARAM = "qwotedUnfilteredFacets";
export const DEFAULT_BROWSE_FILTER = "show_in_default_index:true";

const MARKERS = [DEFAULT_BROWSE_PARAM, UNFILTERED_FACETS_PARAM];
const REFINEMENT_PARAMS = ["filters", "facetFilters", "numericFilters", "tagFilters"];
const FACET_KEYS = ["facets", "facets_stats", "exhaustiveFacetsCount"];

function strip(params) {
  const rest = { ...params };
  const markers = {};
  MARKERS.forEach((key) => {
    markers[key] = !!rest[key];
    delete rest[key];
  });
  return { markers, params: rest };
}

const isBlank = (value) => value === undefined || value === null || value === "" || value.length === 0;

function isDefaultBrowse(markers, params) {
  return (
    markers[DEFAULT_BROWSE_PARAM] &&
    !(params.query || "").trim() &&
    REFINEMENT_PARAMS.every((key) => isBlank(params[key]))
  );
}

export default function withUnfilteredDefaultFacets(client) {
  return {
    ...client,

    search(requests, ...args) {
      // A refined disjunctive facet adds a request per index that omits its own filter, so several
      // requests for one index mean something is refined even if a given request looks bare.
      const requestsPerIndex = {};
      requests.forEach(({ indexName }) => {
        requestsPerIndex[indexName] = (requestsPerIndex[indexName] || 0) + 1;
      });

      const filtered = [];
      const twins = [];
      requests.forEach((request) => {
        const { markers, params } = strip(request.params);
        const defaultBrowse = requestsPerIndex[request.indexName] === 1 && isDefaultBrowse(markers, params);
        filtered.push({ ...request, params: defaultBrowse ? { ...params, filters: DEFAULT_BROWSE_FILTER } : params });
        if (defaultBrowse && markers[UNFILTERED_FACETS_PARAM] && params.facets?.length) {
          twins.push({
            position: filtered.length - 1,
            request: {
              ...request,
              params: { ...params, hitsPerPage: 0, page: 0, analytics: false, clickAnalytics: false },
            },
          });
        }
      });

      const main = client.search(filtered, ...args);
      if (twins.length === 0) return main;

      // A failed twin degrades to the filtered facets instead of failing the search.
      const unfiltered = client
        .search(
          twins.map((t) => t.request),
          ...args,
        )
        .catch(() => null);

      return Promise.all([main, unfiltered]).then(([response, twinResponse]) => {
        if (!twinResponse) return response;
        const results = [...response.results];
        twins.forEach(({ position }, i) => {
          const facetSource = twinResponse.results[i];
          const merged = { ...results[position] };
          FACET_KEYS.forEach((key) => {
            if (facetSource[key] === undefined) delete merged[key];
            else merged[key] = facetSource[key];
          });
          results[position] = merged;
        });
        return { ...response, results };
      });
    },

    searchForFacetValues(requests, ...args) {
      return client.searchForFacetValues(
        requests.map((request) => {
          const { markers, params } = strip(request.params);
          const hideBare = isDefaultBrowse(markers, params) && !markers[UNFILTERED_FACETS_PARAM];
          return { ...request, params: hideBare ? { ...params, filters: DEFAULT_BROWSE_FILTER } : params };
        }),
        ...args,
      );
    },
  };
}
